<template>
  <div class="page-container">
    <!-- 规则类型导航 -->
    <div class="rule-type-nav">
      <div class="rule-tab" :class="{ active: ruleType === 1 }" @click="switchRuleType(1)">开户审核</div>
      <div class="rule-tab" :class="{ active: ruleType === 2 }" @click="switchRuleType(2)">交易审核</div>
    </div>

    <div class="reg-config-layout">
      <!-- 左侧国家配置列表 -->
      <div class="reg-sidebar">
        <div class="sidebar-add-area">
          <el-button
            v-auth="'business:kycConfig:save'"
            v-if="!showCountrySelect"
            type="primary"
            size="small"
            icon="el-icon-plus"
            style="width:100%;"
            @click="openAddCountry">添加国家配置</el-button>
          <div v-else class="select-with-close">
            <el-select
              ref="countrySelectRef"
              v-model="addingCountry"
              filterable
              placeholder="搜索国家..."
              size="small"
              style="flex:1;"
              @change="onAddCountrySelect"
              popper-class="country-select-popper">
              <el-option
                v-for="c in availableCountries"
                :key="c.countryCode"
                :label="c.countryCode + ' - ' + (c.countryCname || c.countryName)"
                :value="c.countryCode">
                <span style="float:left;">{{ c.countryCode }}</span>
                <span style="float:right;color:#8492a6;font-size:13px;">{{ c.countryCname || c.countryName }}</span>
              </el-option>
            </el-select>
            <i class="el-icon-close close-select-btn" @click="cancelAddCountry"></i>
          </div>
        </div>
        <div class="country-list">
          <div
            v-for="c in countries"
            :key="c.countryCode"
            class="country-item"
            :class="{ active: currentCountry === c.countryCode }"
            @click="switchCountry(c.countryCode)">
            <span class="country-icon">{{ isDefaultCountry(c.countryCode) ? '⚙️' : '📍' }}</span>
            <span class="country-name">{{ displayCountryName(c.countryCode) }}</span>
            <el-tag v-if="!isDefaultCountry(c.countryCode) && c.overrideCount > 0" size="mini" type="warning" style="margin-left:6px;">{{ c.overrideCount }}</el-tag>
            <span v-auth="'business:kycConfig:delete'" v-if="!isDefaultCountry(c.countryCode)" class="country-del" @click.stop="deleteCountry(c.countryCode)" title="删除">×</span>
          </div>
        </div>
      </div>

      <!-- 右侧规则详情区 -->
      <div class="reg-content" v-loading="loading">
        <div v-if="currentCountry !== 'DEFAULT'" class="inherit-bar">
          <span>当前继承自默认配置{{ overrideCount > 0 ? `，已覆盖 ${overrideCount} 项` : '' }}</span>
          <div class="inherit-actions">
            <el-button v-auth="'business:kycConfig:edit'" size="mini" @click="viewDiff">查看差异</el-button>
            <el-button v-auth="'business:kycConfig:edit'" size="mini" type="warning" @click="resetOverride">恢复默认</el-button>
          </div>
        </div>

        <!-- 基础信息 -->
        <div class="cfg-card">
          <div class="cfg-card-header">
            <span>📋 基础信息</span>
          </div>
          <div class="cfg-card-body">
            <div class="cfg-row">
              <span class="cfg-label">规则名称</span>
              <el-input v-model="form.ruleName" placeholder="请输入规则名称" size="small" style="width:320px;"></el-input>
            </div>
            <div class="cfg-row" v-if="ruleType === 2">
              <span class="cfg-label">规则描述</span>
              <el-input v-model="form.ruleDesc" placeholder="规则用途说明" size="small" style="width:320px;"></el-input>
            </div>
            <div class="cfg-row">
              <span class="cfg-label">状态</span>
              <el-radio-group v-model="form.status">
                <el-radio :label="1">启用</el-radio>
                <el-radio :label="2">停用</el-radio>
              </el-radio-group>
            </div>
          </div>
        </div>

        <!-- 触发场景 -->
        <div class="cfg-card" style="margin-top:16px;">
          <div class="cfg-card-header">
            <span>🎯 触发场景{{ ruleType === 2 ? '（多选）' : '' }}</span>
          </div>
          <div class="cfg-card-body">
            <div class="cfg-row" v-if="ruleType === 1">
              <span class="cfg-label">业务动作</span>
              <div>
                <el-input value="用户注册提交" disabled size="small" style="width:220px;"></el-input>
                <div class="form-text">固定为"用户注册提交"，不可修改</div>
              </div>
            </div>
            <template v-else>
              <div class="cfg-row">
                <span class="cfg-label">业务动作</span>
                <div class="check-group">
                  <el-checkbox v-for="s in sceneOptions" :key="s.value" v-model="s.checked">{{ s.label }}</el-checkbox>
                </div>
              </div>
              <div class="cfg-row">
                <span class="cfg-label">自定义场景</span>
                <div>
                  <el-button v-auth="'business:kycConfig:sceneSave'" size="small" type="primary" plain icon="el-icon-plus" @click="openSceneModal()">添加自定义场景</el-button>
                  <div class="scene-chips">
                    <span
                      v-for="scene in sceneLibrary"
                      :key="scene.id"
                      class="scene-chip"
                      :class="{ checked: form.sceneIds.includes(scene.id) }"
                      @click="toggleScene(scene.id)">
                      <span class="chip-icon">🏷️</span>
                      <span>{{ scene.sceneName }}</span>
                      <span v-auth="'business:kycConfig:edit'" class="chip-edit" @click.stop="openSceneModal(scene)">✎</span>
                      <span v-auth="'business:kycConfig:sceneDelete'" class="chip-delete" @click.stop="deleteScene(scene.id)">✕</span>
                      <el-tooltip effect="dark" placement="top">
                        <div slot="content">
                          基础事件: {{ sceneBaseEventLabel(scene.baseEvent) }}<br/>
                          使用数: {{ scene.usedCount }} 条规则
                        </div>
                        <i class="el-icon-info" style="font-size:12px;color:#909399;"></i>
                      </el-tooltip>
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- 异常识别规则 -->
        <div class="cfg-card" style="margin-top:16px;">
          <div class="cfg-card-header">
            <span>⚠️ 异常识别规则</span>
            <el-button v-auth="'business:kycConfig:save'" size="small" type="primary" icon="el-icon-plus" style="margin-left:auto;" @click="openAnomalyModal()">新增规则</el-button>
          </div>
          <div class="cfg-card-body">
            <el-table :data="form.anomalyRules" size="small" border>
              <el-table-column label="规则名称" prop="ruleName" min-width="160"></el-table-column>
              <el-table-column label="触发条件" prop="condDesc" min-width="200">
                <template slot-scope="{ row }">
                  <span style="font-size:12px;color:#909399;">{{ row.condDesc }}</span>
                </template>
              </el-table-column>
              <el-table-column label="动作" width="150">
                <template slot-scope="{ row }">
                  <el-select v-model="row.actionType" size="mini" @change="rowChanged">
                    <el-option v-for="o in actionOptions" :key="o.value" :label="o.label" :value="o.value"></el-option>
                  </el-select>
                </template>
              </el-table-column>
              <el-table-column label="启用" width="90">
                <template slot-scope="{ row }">
                  <el-switch v-model="row.enabled" :active-value="1" :inactive-value="2" @change="rowChanged"></el-switch>
                </template>
              </el-table-column>
              <el-table-column label="编辑" width="70">
                <template slot-scope="{ row }">
                  <el-button v-auth="'business:kycConfig:save'" size="mini" type="text" @click="openAnomalyModal(row)">编辑</el-button>
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-if="!form.anomalyRules.length" description="暂无异常识别规则"></el-empty>
          </div>
        </div>

        <div class="config-footer">
          <el-button @click="resetForm">重置</el-button>
          <el-button v-auth="'business:kycConfig:save'" type="primary" @click="saveConfig">保存配置</el-button>
        </div>
      </div>
    </div>

    <!-- 异常规则编辑弹窗 -->
    <el-dialog :title="anomalyModalTitle" :visible.sync="anomalyVisible" width="560px" :close-on-click-modal="false">
      <el-form :model="anomalyForm" label-width="90px" size="small">
        <el-form-item label="规则名称" required>
          <el-input v-model="anomalyForm.ruleName" placeholder="请输入规则名称"></el-input>
        </el-form-item>
        <el-form-item label="触发条件">
          <el-input v-model="anomalyForm.condDesc" type="textarea" :rows="2" placeholder="请输入触发条件描述"></el-input>
        </el-form-item>
        <el-form-item label="条件配置">
          <el-radio-group v-model="anomalyForm.condMode" @change="onCondModeChange">
            <el-radio :label="1">自定义JSON条件</el-radio>
            <el-radio :label="2">关联标签</el-radio>
          </el-radio-group>
          <el-input
            v-if="anomalyForm.condMode === 1"
            v-model="anomalyForm.jsonConfig"
            type="textarea"
            :rows="6"
            class="json-textarea"
            placeholder='请输入JSON格式条件配置，例如：{"field":"device_id","operator":">=","value":3}'></el-input>
          <div v-else style="margin-top:10px;">
            <div class="form-hint" style="margin-bottom:6px;">标签组合逻辑</div>
            <el-radio-group v-model="anomalyForm.tagLogic">
              <el-radio label="AND">AND（同时满足所有标签）</el-radio>
              <el-radio label="OR">OR（满足任一标签即可）</el-radio>
            </el-radio-group>
            <div class="form-hint" style="margin:10px 0 6px;">选择标签（可跨类别多选）</div>
            <el-select v-model="anomalyForm.tagIds" multiple filterable placeholder="请选择标签" style="width:100%;">
              <el-option v-for="t in tagOptions" :key="t.id" :label="t.tagName" :value="t.id"></el-option>
            </el-select>
          </div>
        </el-form-item>
        <el-form-item label="处理动作">
          <el-select v-model="anomalyForm.actionType" style="width:100%;">
            <el-option v-for="o in actionOptions" :key="o.value" :label="o.label" :value="o.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="启用状态">
          <el-checkbox v-model="anomalyForm.enabled" :true-label="1" :false-label="2">启用该规则</el-checkbox>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="anomalyVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="saveAnomaly">保存</el-button>
      </div>
    </el-dialog>

    <!-- 自定义触发场景弹窗 -->
    <el-dialog :title="sceneModalTitle" :visible.sync="sceneVisible" width="620px" :close-on-click-modal="false">
      <el-form :model="sceneForm" label-width="100px" size="small">
        <el-form-item label="场景名称" required>
          <el-input v-model="sceneForm.sceneName" placeholder="清晰命名以便复用，如大额出金-高风险国家"></el-input>
        </el-form-item>
        <el-form-item label="基础事件类型" required>
          <el-select v-model="sceneForm.baseEvent" style="width:100%;">
            <el-option v-for="b in baseEventOptions" :key="b.value" :label="b.label" :value="b.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="附加条件">
          <div v-for="(cond, idx) in sceneForm.conditions" :key="idx" class="scene-cond-row">
            <el-select v-model="cond.logic" style="width:70px;" :disabled="idx === 0">
              <el-option v-for="l in ['AND', 'OR']" :key="l" :label="l" :value="l"></el-option>
            </el-select>
            <el-select v-model="cond.fieldKey" style="flex:1;" placeholder="字段" @change="onSceneFieldChange(cond)">
              <el-option-group v-for="group in sceneFieldGroups" :key="group.name" :label="group.name">
                <el-option v-for="f in group.fields" :key="f.key" :label="f.label" :value="f.key"></el-option>
              </el-option-group>
            </el-select>
            <el-select v-model="cond.operator" style="width:120px;" placeholder="操作符">
              <el-option v-for="op in sceneOperators" :key="op" :label="op" :value="op"></el-option>
            </el-select>
            <el-input v-model="cond.value" placeholder="值" style="width:110px;"></el-input>
            <el-select v-if="cond.hasCurrency" v-model="cond.currency" style="width:80px;" placeholder="币种">
              <el-option v-for="c in ['USD', 'CNY', 'EUR', 'GBP', 'HKD']" :key="c" :label="c" :value="c"></el-option>
            </el-select>
            <el-button type="text" icon="el-icon-delete" @click="sceneForm.conditions.splice(idx, 1)"></el-button>
          </div>
          <el-button size="mini" type="primary" plain icon="el-icon-plus" @click="addSceneCondition">添加条件</el-button>
          <div class="form-hint">字段来源：交易类（金额、币种、账户组、杠杆倍数）、用户属性（国家、KYC等级、风险标签、注册时长）、行为（触发频次、时间窗口、设备指纹）</div>
        </el-form-item>
        <el-form-item label="场景描述">
          <el-input v-model="sceneForm.description" type="textarea" :rows="2" placeholder="选填，便于其他管理员理解用途"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="sceneVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="saveScene">保存场景</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { kycConfigGetByCountry, kycConfigSave, kycConfigDelete, kycSceneList, kycSceneSave, kycSceneDelete } from '@/api/kycConfig'
import { countryList } from '@/api/regConfig'
import { tagListAll } from '@/api/tagManagement'

const DEFAULT_ANOMALY = () => ({
  rowKey: null, id: null, ruleName: '', condDesc: '', actionType: 'force_manual',
  enabled: 1, condMode: 1, jsonConfig: '', tagLogic: 'AND', tagIds: []
})

const DEFAULT_SCENE = () => ({
  id: null, sceneName: '', baseEvent: 'withdrawal', description: '', conditions: []
})

const sceneFieldGroups = [
  { name: '交易类', fields: [
    { key: 'amount', label: '单笔金额', hasCurrency: true },
    { key: 'currency', label: '币种', hasCurrency: false },
    { key: 'account_group', label: '账户组', hasCurrency: false },
    { key: 'leverage', label: '杠杆倍数', hasCurrency: false }
  ]},
  { name: '用户属性', fields: [
    { key: 'user_country', label: '用户国家', hasCurrency: false },
    { key: 'kyc_level', label: 'KYC等级', hasCurrency: false },
    { key: 'risk_tag', label: '风险标签', hasCurrency: false },
    { key: 'reg_duration', label: '注册时长', hasCurrency: false }
  ]},
  { name: '行为', fields: [
    { key: 'trigger_freq', label: '触发频次', hasCurrency: false },
    { key: 'time_window', label: '时间窗口', hasCurrency: false },
    { key: 'device_fingerprint', label: '设备指纹', hasCurrency: false }
  ]}
]

const sceneOperators = ['>', '>=', '<', '<=', '=', '!=', 'in', 'not in', 'count_distinct']
const baseEventOptions = [
  { value: 'withdrawal', label: '出金' },
  { value: 'deposit', label: '入金' },
  { value: 'login', label: '登录' },
  { value: 'open_account', label: '开通账户' },
  { value: 'cross_currency', label: '跨币种转换' },
  { value: 'modify_card', label: '修改绑卡信息' },
  { value: 'submit_kyc', label: '提交KYC' },
  { value: 'change_password', label: '修改密码' },
  { value: 'other', label: '其他' }
]

const actionOptions = [
  { value: 'force_manual', label: '强制转人工' },
  { value: 'auto_limit', label: '自动限制注册' },
  { value: 'ban_open', label: '禁止开仓' },
  { value: 'ban_close', label: '禁止平仓' },
  { value: 'ban_withdraw', label: '禁止出金' },
  { value: 'withdraw_manual', label: '出金人工审批' },
  { value: 'auto_reject', label: '自动拒绝' },
  { value: 'flag', label: '标记' }
]

let rowKeySeq = 0

export default {
  name: 'KycConfig',
  data() {
    return {
      ruleType: 1,
      currentCountry: 'DEFAULT',
      countries: [],
      loading: false,
      form: { ruleName: '', ruleDesc: '', status: 1, triggerScenes: [], anomalyRules: [], sceneIds: [] },
      sceneOptions: [
        { value: 'withdrawal', label: '出金', checked: false },
        { value: 'high_leverage_account', label: '开通高杠杆账户', checked: false },
        { value: 'cross_currency', label: '跨币种转换', checked: false }
      ],
      sceneLibrary: [],
      allCountries: [],
      showCountrySelect: false,
      addingCountry: null,
      tagOptions: [],
      anomalyVisible: false,
      anomalyModalTitle: '新增异常识别规则',
      anomalyForm: DEFAULT_ANOMALY(),
      sceneVisible: false,
      sceneModalTitle: '新建自定义触发场景',
      sceneForm: DEFAULT_SCENE(),
      sceneFieldGroups,
      sceneOperators,
      baseEventOptions,
      actionOptions
    }
  },
  computed: {
    availableCountries() {
      return this.allCountries.filter(c => c.countryCode && !this.isDefaultCountry(c.countryCode) && !this.countries.find(x => x.countryCode === c.countryCode))
    },
    overrideCount() {
      const found = this.countries.find(c => c.countryCode === this.currentCountry)
      return found ? Number(found.overrideCount || 0) : 0
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.fetchCountries()
      this.fetchTags()
      this.fetchSceneLibrary()
      this.loadConfig()
    },
    fetchCountries() {
      countryList({ pageNum: 1, pageSize: 500 }).then(res => {
        if (res.code === 0) this.allCountries = res.result.list || []
      })
    },
    fetchTags() {
      tagListAll().then(res => { if (res.code === 0) this.tagOptions = res.result || [] })
    },
    fetchSceneLibrary() {
      kycSceneList().then(res => { if (res.code === 0) this.sceneLibrary = res.result || [] })
    },
    normalizeCountries(list) {
      // 归一化：DEFAULT 与空串均视为默认配置，合并为一项
      const seen = {}
      return (list || []).filter(c => {
        const code = this.isDefaultCountry(c.countryCode) ? 'DEFAULT' : c.countryCode
        if (seen[code]) return false
        seen[code] = true
        return true
      }).map(c => ({
        countryCode: this.isDefaultCountry(c.countryCode) ? 'DEFAULT' : c.countryCode,
        overrideCount: c.overrideCount || 0
      }))
    },
    loadConfig() {
      this.loading = true
      kycConfigGetByCountry(this.ruleType, this.currentCountry).then(res => {
        if (res.code === 0 && res.result) {
          const d = res.result
          this.countries = this.normalizeCountries(d.countries || [])
          this.form.ruleName = d.ruleName || ''
          this.form.ruleDesc = d.ruleDesc || ''
          this.form.status = d.status || 1
          this.form.anomalyRules = (d.anomalyRules || []).map(r => ({ ...r, rowKey: ++rowKeySeq }))
          this.form.sceneIds = (d.scenes || []).map(s => s.id)
          const scenes = d.triggerScenes || []
          this.sceneOptions.forEach(s => { s.checked = scenes.includes(s.value) })
          if (this.ruleType === 1) {
            this.form.triggerScenes = ['用户注册提交']
          } else {
            this.form.triggerScenes = scenes
          }
        }
      }).finally(() => { this.loading = false })
    },
    isDefaultCountry(code) { return !code || code === 'DEFAULT' },
    displayCountryName(code) {
      if (this.isDefaultCountry(code)) return '默认配置'
      const info = this.allCountries.find(a => a.countryCode === code)
      const name = info ? (info.countryCname || info.countryName) : ''
      return name ? `${code} - ${name}` : code
    },
    switchRuleType(type) {
      if (this.ruleType === type) return
      this.ruleType = type
      this.loadConfig()
    },
    switchCountry(code) {
      this.currentCountry = code
      this.loadConfig()
    },
    openAddCountry() {
      this.showCountrySelect = true
      this.addingCountry = null
      this.$nextTick(() => { this.$refs.countrySelectRef && this.$refs.countrySelectRef.focus() })
    },
    cancelAddCountry() { this.showCountrySelect = false; this.addingCountry = null },
    onAddCountrySelect(code) {
      if (!code) return
      this.countries.push({ countryCode: code, overrideCount: 0 })
      this.currentCountry = code
      this.loadConfig()
      this.cancelAddCountry()
    },
    deleteCountry(code) {
      this.$confirm(`确定删除 ${code} 的配置吗？`, '提示', { type: 'warning' }).then(() => {
        kycConfigDelete(this.ruleType, code).then(res => {
          if (res.code !== 0) { this.$message.error(res.message || '删除失败'); return }
          this.countries = this.countries.filter(c => c.countryCode !== code)
          if (this.currentCountry === code) { this.currentCountry = 'DEFAULT'; this.loadConfig() }
          this.$message.success('已删除')
        })
      }).catch(() => {})
    },
    resetOverride() {
      this.$confirm('确定恢复为默认配置吗？将删除该国家的自定义配置。', '提示', { type: 'warning' }).then(() => {
        kycConfigDelete(this.ruleType, this.currentCountry).then(res => {
          if (res.code !== 0) { this.$message.error(res.message || '操作失败'); return }
          this.countries = this.countries.filter(c => c.countryCode !== this.currentCountry)
          this.currentCountry = 'DEFAULT'
          this.loadConfig()
          this.$message.success('已恢复默认配置')
        })
      }).catch(() => {})
    },
    viewDiff() { this.$message.info('当前配置与默认配置的差异项已在国家列表中通过数字标注展示') },
    toggleScene(id) {
      const idx = this.form.sceneIds.indexOf(id)
      if (idx >= 0) this.form.sceneIds.splice(idx, 1)
      else this.form.sceneIds.push(id)
    },
    sceneBaseEventLabel(value) {
      const found = baseEventOptions.find(b => b.value === value)
      return found ? found.label : value
    },
    resetForm() {
      this.$confirm('确定重置当前配置吗？', '提示', { type: 'warning' }).then(() => {
        this.form.ruleName = ''
        this.form.ruleDesc = ''
        this.form.status = 1
        this.form.anomalyRules = []
        this.form.sceneIds = []
        this.sceneOptions.forEach(s => { s.checked = false })
      }).catch(() => {})
    },
    rowChanged() {},
    openAnomalyModal(row) {
      if (row) {
        this.anomalyModalTitle = '编辑异常识别规则'
        this.anomalyForm = {
          rowKey: row.rowKey, id: row.id, ruleName: row.ruleName, condDesc: row.condDesc,
          actionType: row.actionType, enabled: row.enabled, condMode: row.condMode || 1,
          jsonConfig: row.jsonConfig || '', tagLogic: row.tagLogic || 'AND', tagIds: row.tagIds || []
        }
      } else {
        this.anomalyModalTitle = '新增异常识别规则'
        this.anomalyForm = { ...DEFAULT_ANOMALY(), rowKey: ++rowKeySeq }
      }
      this.anomalyVisible = true
    },
    onCondModeChange() {},
    saveAnomaly() {
      if (!this.anomalyForm.ruleName) { this.$message.warning('请输入规则名称'); return }
      const f = this.anomalyForm
      const row = {
        rowKey: f.rowKey, id: f.id, ruleName: f.ruleName, condDesc: f.condDesc,
        actionType: f.actionType, enabled: f.enabled, condMode: f.condMode,
        jsonConfig: f.condMode === 1 ? f.jsonConfig : null,
        tagLogic: f.condMode === 2 ? f.tagLogic : null,
        tagIds: f.condMode === 2 ? f.tagIds : null
      }
      const idx = this.form.anomalyRules.findIndex(r => r.rowKey === f.rowKey)
      if (idx >= 0) this.form.anomalyRules.splice(idx, 1, row)
      else this.form.anomalyRules.push(row)
      this.anomalyVisible = false
    },
    openSceneModal(scene) {
      if (scene) {
        this.sceneModalTitle = '编辑自定义触发场景'
        this.sceneForm = {
          id: scene.id, sceneName: scene.sceneName, baseEvent: scene.baseEvent,
          description: scene.description, conditions: this.parseConditions(scene.conditionsJson)
        }
      } else {
        this.sceneModalTitle = '新建自定义触发场景'
        this.sceneForm = { ...DEFAULT_SCENE() }
      }
      if (!this.sceneForm.conditions.length) this.sceneForm.conditions = [this.emptyCondition()]
      this.sceneVisible = true
    },
    parseConditions(json) {
      if (!json) return []
      try {
        const arr = JSON.parse(json)
        const conditions = []
        arr.forEach(item => {
          if (item.logic && !item.field) {
            const prev = conditions[conditions.length - 1]
            if (prev) prev.logic = item.logic
          } else {
            conditions.push({
              fieldKey: item.fieldKey || '', field: item.field || '',
              operator: item.operator || '', value: item.value != null ? String(item.value) : '',
              currency: item.currency || '', logic: 'AND', hasCurrency: !!item.currency
            })
          }
        })
        return conditions
      } catch (e) {
        return []
      }
    },
    emptyCondition() {
      return { fieldKey: '', field: '', operator: '>', value: '', currency: '', logic: 'AND', hasCurrency: false }
    },
    addSceneCondition() { this.sceneForm.conditions.push(this.emptyCondition()) },
    onSceneFieldChange(cond) {
      const found = sceneFieldGroups.flatMap(g => g.fields).find(f => f.key === cond.fieldKey)
      cond.field = found ? found.label : cond.fieldKey
      cond.hasCurrency = found ? !!found.hasCurrency : false
      if (!cond.hasCurrency) cond.currency = ''
    },
    saveScene() {
      if (!this.sceneForm.sceneName) { this.$message.warning('请输入场景名称'); return }
      if (!this.sceneForm.baseEvent) { this.$message.warning('请选择基础事件类型'); return }
      const validConditions = this.sceneForm.conditions.filter(c => c.fieldKey && c.value !== '')
      const conditionsJson = JSON.stringify(validConditions)
      kycSceneSave({
        id: this.sceneForm.id || undefined,
        sceneName: this.sceneForm.sceneName,
        baseEvent: this.sceneForm.baseEvent,
        conditionsJson,
        description: this.sceneForm.description
      }).then(res => {
        if (res.code !== 0) { this.$message.error(res.message || '保存失败'); return }
        this.$message.success('场景保存成功')
        this.sceneVisible = false
        this.fetchSceneLibrary()
        // 新建的场景自动勾选进当前配置（编辑已有场景时不重复添加）
        if (!this.sceneForm.id && res.result) {
          const id = Number(res.result)
          if (this.form.sceneIds.indexOf(id) < 0) this.form.sceneIds.push(id)
        }
      })
    },
    deleteScene(id) {
      this.$confirm('确定删除该自定义场景吗？删除后规则关联将一并移除。', '提示', { type: 'warning' }).then(() => {
        kycSceneDelete(id).then(res => {
          if (res.code !== 0) { this.$message.error(res.message || '删除失败'); return }
          this.form.sceneIds = this.form.sceneIds.filter(s => s !== id)
          this.fetchSceneLibrary()
          this.$message.success('已删除')
        })
      }).catch(() => {})
    },
    saveConfig() {
      const triggerScenes = this.ruleType === 1
        ? ['用户注册提交']
        : this.sceneOptions.filter(s => s.checked).map(s => s.value)
      const data = {
        ruleType: this.ruleType,
        countryCode: this.currentCountry,
        ruleName: this.form.ruleName,
        status: this.form.status,
        ruleDesc: this.ruleType === 2 ? this.form.ruleDesc : null,
        triggerScenes,
        sceneIds: this.form.sceneIds,
        anomalyRules: this.form.anomalyRules.map(r => ({
          id: r.id, ruleName: r.ruleName, condDesc: r.condDesc, actionType: r.actionType,
          enabled: r.enabled, condMode: r.condMode,
          jsonConfig: r.condMode === 1 ? r.jsonConfig : null,
          tagLogic: r.condMode === 2 ? r.tagLogic : null,
          tagIds: r.condMode === 2 ? (r.tagIds || []) : null
        }))
      }
      kycConfigSave(data).then(res => {
        if (res.code !== 0) { this.$message.error(res.message || '保存失败'); return }
        this.$message.success('配置保存成功')
        this.loadConfig()
      })
    }
  }
}
</script>

<style scoped>
.page-container { padding: 16px; background: #f5f7fa; min-height: calc(100vh - 84px); }
.rule-type-nav { display: flex; gap: 0; margin-bottom: 16px; background: #fff; border-radius: 4px; padding: 6px; width: fit-content; border: 1px solid #ebeef5; }
.rule-tab { padding: 8px 24px; border-radius: 4px; cursor: pointer; font-size: 13px; color: #606266; }
.rule-tab.active { background: var(--brand-primary); color: #fff; }
.reg-config-layout { display: flex; gap: 16px; min-height: 600px; }
.reg-sidebar { width: 220px; min-width: 220px; background: #fff; border-radius: 4px; padding: 12px; }
.sidebar-add-area { margin-bottom: 10px; }
.select-with-close { display: flex; align-items: center; gap: 4px; }
.close-select-btn { font-size: 16px; color: #909399; cursor: pointer; padding: 4px; border-radius: 2px; }
.close-select-btn:hover { color: #f56c6c; }
.country-list { margin-top: 4px; }
.country-item { display: flex; align-items: center; gap: 6px; padding: 8px 10px; border-radius: 4px; cursor: pointer; font-size: 13px; color: #333; margin-bottom: 2px; }
.country-item:hover { background: #f0f2f5; }
.country-item.active { background: var(--brand-primary-light); color: var(--brand-primary); font-weight: 500; }
.country-icon { font-size: 14px; width: 20px; text-align: center; flex-shrink: 0; }
.country-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: flex; align-items: center; }
.country-del { margin-left: auto; color: #c0c4cc; font-size: 16px; cursor: pointer; padding: 0 4px; }
.country-del:hover { color: #f56c6c; }
.reg-content { flex: 1; background: #fff; border-radius: 4px; padding: 20px; min-height: 500px; }
.inherit-bar { display: flex; align-items: center; justify-content: space-between; background: #f0f9eb; border: 1px solid #e1f3d8; border-radius: 4px; padding: 8px 14px; margin-bottom: 16px; font-size: 12px; color: #67c23a; }
.inherit-actions { display: flex; gap: 4px; }
.cfg-card { border: 1px solid #e4e7ed; border-radius: 6px; overflow: hidden; }
.cfg-card-header { display: flex; align-items: center; gap: 8px; padding: 10px 16px; background: #fafafa; font-size: 13px; font-weight: 500; border-bottom: 1px solid #e4e7ed; }
.cfg-card-body { padding: 16px; }
.cfg-row { display: flex; align-items: flex-start; padding: 8px 0; border-bottom: 1px dashed #f0f0f0; }
.cfg-row:last-child { border-bottom: none; }
.cfg-label { width: 120px; min-width: 120px; font-size: 12px; color: #606266; padding-top: 4px; }
.form-text { font-size: 12px; color: #909399; margin-top: 4px; }
.check-group { display: flex; flex-wrap: wrap; gap: 8px 20px; }
.scene-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.scene-chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border: 1px solid #dcdfe6; border-radius: 14px; font-size: 12px; cursor: pointer; color: #606266; background: #fff; transition: all .15s; }
.scene-chip:hover { border-color: var(--brand-primary); }
.scene-chip.checked { background: var(--brand-primary-light); border-color: var(--brand-primary); color: var(--brand-primary); }
.chip-icon { font-size: 12px; }
.chip-edit { cursor: pointer; color: #909399; }
.chip-edit:hover { color: var(--brand-primary); }
.chip-delete { cursor: pointer; color: #909399; }
.chip-delete:hover { color: #f56c6c; }
.config-footer { margin-top: 20px; padding-top: 16px; border-top: 1px solid #ebeef5; display: flex; justify-content: flex-end; gap: 8px; }
.json-textarea { font-family: 'Consolas', 'Menlo', monospace; }
.form-hint { font-size: 12px; color: #909399; }
.scene-cond-row { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
</style>
